import React, { useEffect, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  TooltipItem,
} from "chart.js";

Chart.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

// ---------- Types ----------
interface CommunityData {
  community_centres: number;
  quranic_schools: number;
  water_kiosks: number;
  small_shops: number;
  sanitation_facilities: number;
  markets: number;
  community_health_centres: number;
  other_community_facility_count: number;
}

interface HumanitarianData {
  schools: number;
  health_centres: number;
  distribution_centres: number;
  protection_counselling: number;
  solar_lights: number;
  water_points: number;
  latrines: number;
  nutrition_centres: number;
  psn_houses: number;
  other_humanitarian_facility_count: number;
}

type InfrastructureType = "community" | "humanitarian";

interface Props {
  data: CommunityData | HumanitarianData | null;
  type: InfrastructureType;
  year: string | number;
  region?: string | null;
  isLoading?: boolean;
  id: string;
  title?: string;
}

// ---------- Configs ----------
const communityConfig = [
  { key: "community_centres", label: "Community Centres" },
  { key: "quranic_schools", label: "Quranic Schools" },
  { key: "water_kiosks", label: "Water Kiosks" },
  { key: "small_shops", label: "Small Shops" },
  { key: "sanitation_facilities", label: "Sanitation" },
  { key: "markets", label: "Markets" },
  { key: "community_health_centres", label: "Health Centres" },
  { key: "other_community_facility_count", label: "Other Facilities" },
] as const;

const humanitarianConfig = [
  { key: "schools", label: "Schools" },
  { key: "health_centres", label: "Health Centres" },
  { key: "distribution_centres", label: "Distribution Centres" },
  { key: "protection_counselling", label: "Protection Counselling" },
  { key: "solar_lights", label: "Solar Lights" },
  { key: "water_points", label: "Water Points" },
  { key: "latrines", label: "Latrines" },
  { key: "nutrition_centres", label: "Nutrition Centres" },
  { key: "psn_houses", label: "PSN Houses" },
  { key: "other_humanitarian_facility_count", label: "Other Facilities" },
] as const;

const colorScale = [
  "#D6D6D6",
  "#BFBFBF",
  "#A9A9A9",
  "#949494",
  "#7F7F7F",
  "#4B4B4B",
];

// ---------- Component ----------
const InfrastructureBarChart: React.FC<Props> = ({
  data,
  type,
  year,
  region,
  isLoading = false,
  id,
  title,
}) => {
  useEffect(() => {
    return () => {
      Chart.getChart(id)?.destroy();
    };
  }, [id]);

  const chartData = useMemo(() => {
    if (!data) return null;

    const config = type === "community" ? communityConfig : humanitarianConfig;

    const labels = config.map((item) => item.label);
    const values = config.map((item) => (data as any)[item.key] ?? 0);

    const min = Math.min(...values);
    const max = Math.max(...values);
    const step = max === min ? 1 : (max - min) / colorScale.length;

    const backgroundColor = values.map((v) => {
      const index = Math.floor((v - min) / step);
      return colorScale[Math.min(index, colorScale.length - 1)];
    });

    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor,
          borderColor: "#fff",
          borderWidth: 1,
          barThickness: 28,
          maxBarThickness: 36,
          categoryPercentage: 0.7,
          barPercentage: 0.85,
        },
      ],
    };
  }, [data, type]);

  // Title now uses year + region when available
  const chartTitle =
    title ||
    `${type === "community" ? "Community" : "Humanitarian"} Infrastructure` +
      ` - ${year}` +
      (region ? ` (${region})` : " (All Regions)");

  return (
    <div
      className="p-2 shadow-md rounded-md flex items-center justify-center w-full min-w-0 overflow-hidden"
      style={{ backgroundColor: "#f8f9fa" }}
    >
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <svg
            className="w-5 h-5 animate-spin text-gray-700"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          Loading chart data...
        </div>
      ) : chartData ? (
        <div style={{ minHeight: "260px", width: "100%", height: "100%" }}>
          <Bar
            id={id}
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                title: {
                  display: true,
                  text: chartTitle,
                  font: { size: 12, weight: "bold" },
                  padding: { top: 0, bottom: 12 },
                  align: "center",
                },
                tooltip: {
                  backgroundColor: "#f1f1f1",
                  borderColor: "#ccc",
                  borderWidth: 1,
                  bodyColor: "#333",
                  padding: 10,
                  callbacks: {
                    label: (context: TooltipItem<"bar">) => {
                      const value = context.parsed.y ?? 0;
                      return `${context.label}: ${value.toLocaleString()}`;
                    },
                  },
                },
              },
              scales: {
                x: {
                  type: "category",
                  grid: { display: false },
                  ticks: {
                    font: { size: 10 },
                    maxRotation: 0,
                    minRotation: 0,
                    callback: function (tickValue, index) {
                      const label = chartData.labels[index] ?? "";
                      return label.length > 11
                        ? label.slice(0, 11) + "…"
                        : label;
                    },
                  },
                },
                y: {
                  beginAtZero: true,
                  grid: { display: false },
                  ticks: {
                    font: { size: 10 },
                    callback: function (tickValue) {
                      if (typeof tickValue !== "number") return tickValue;
                      if (tickValue >= 1_000_000)
                        return (
                          (tickValue / 1_000_000)
                            .toFixed(1)
                            .replace(/\.0$/, "") + "M"
                        );
                      if (tickValue >= 1_000)
                        return (tickValue / 1_000).toFixed(0) + "K";
                      return tickValue.toString();
                    },
                  },
                },
              },
              elements: {
                bar: {
                  borderRadius: [4, 4, 0, 0],
                },
              },
            }}
          />
        </div>
      ) : (
        <p className="text-sm text-gray-500">No data available.</p>
      )}
    </div>
  );
};

export default InfrastructureBarChart;